import { canonicalUrl } from "../../../shared/src/seo";
import { regularPrices } from "../../../shared/src/platform-plans";
import proCheckout from "./pro-checkout.json";

/**
 * Schema.org descriptions for search engines. Built only from what the page already shows:
 * published, public metadata, never member-only fields.
 */
export type StructuredData = Record<string, unknown>;

export const platformSocialProfiles = [
  { label: "Instagram", url: "https://www.instagram.com/drate.app/" },
  { label: "TikTok", url: "https://www.tiktok.com/@drateapp" },
  { label: "X", url: "https://x.com/drate_app" },
  { label: "YouTube", url: "https://www.youtube.com/@drateapp" },
  { label: "Crunchbase", url: "https://www.crunchbase.com/organization/drate" },
] as const;

function identityProfileUrls(socials: readonly { url: string }[]) {
  return [
    ...new Set(
      socials.flatMap(({ url }) => {
        try {
          const profile = new URL(url);
          if (
            profile.protocol !== "https:" ||
            profile.username ||
            profile.password ||
            profile.port ||
            profile.search ||
            profile.hash
          )
            return [];
          const host = profile.hostname.toLowerCase().replace(/^www\./, "");
          const path = profile.pathname.replace(/\/$/, "");
          const profilePaths: Record<string, RegExp> = {
            "instagram.com": /^\/[a-zA-Z0-9._]+$/,
            "tiktok.com": /^\/@[a-zA-Z0-9._]+$/,
            "x.com": /^\/[a-zA-Z0-9_]+$/,
            "twitter.com": /^\/[a-zA-Z0-9_]+$/,
            "youtube.com": /^\/(?:@[a-zA-Z0-9._-]+|channel\/[a-zA-Z0-9_-]+)$/,
            "twitch.tv": /^\/[a-zA-Z0-9_]+$/,
          };
          return profilePaths[host]?.test(path) ? [profile.href] : [];
        } catch {
          return [];
        }
      }),
    ),
  ];
}

const price = (cents: number) => (cents / 100).toFixed(2);

/** Drate itself: the company, its site and the product with its two plans. */
/** Drate's own record. `regular`: plans.regular is on, so the offers are Regular and Pro. */
export function platformStructuredData(origin: string, regular = false): StructuredData {
  const home = canonicalUrl(origin, "/");
  const organization = `${home}#organization`;
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": organization,
        name: "Drate",
        alternateName: ["Drate app", "drate.app"],
        url: home,
        logo: canonicalUrl(origin, "/logo.png"),
        description:
          "Drate is a membership website builder for creators: your own website, memberships, content, coaching, community and merch on your own domain.",
        sameAs: platformSocialProfiles.map((profile) => profile.url),
        contactPoint: {
          "@type": "ContactPoint",
          contactType: "customer support",
          email: "support@drate.app",
        },
      },
      {
        "@type": "WebSite",
        "@id": `${home}#website`,
        name: "Drate",
        alternateName: "Drate app",
        url: home,
        publisher: { "@id": organization },
      },
      {
        "@type": "SoftwareApplication",
        name: "Drate",
        url: home,
        applicationCategory: "BusinessApplication",
        operatingSystem: "Web",
        description:
          "Your own website with memberships built in: content, memberships, coaching, community and merch on your own domain.",
        publisher: { "@id": organization },
        offers: [
          ...(regular
            ? [
                {
                  "@type": "Offer",
                  name: "Regular",
                  price: price(regularPrices.month.amount),
                  priceCurrency: "USD",
                  description: "Monthly, plus a 5% platform fee on membership sales.",
                },
                {
                  "@type": "Offer",
                  name: "Regular, billed yearly",
                  price: price(regularPrices.year.amount),
                  priceCurrency: "USD",
                  description: "Yearly, plus a 5% platform fee on membership sales.",
                },
              ]
            : [
                {
                  "@type": "Offer",
                  name: "Free",
                  price: "0.00",
                  priceCurrency: "USD",
                  description: "5% platform fee on membership sales.",
                },
              ]),
          {
            "@type": "Offer",
            name: "Pro",
            price: price(proCheckout.month.amount),
            priceCurrency: proCheckout.month.currency.toUpperCase(),
            description: "Monthly, plus a 5% platform fee on membership sales.",
          },
          {
            "@type": "Offer",
            name: "Pro, billed yearly",
            price: price(proCheckout.year.amount),
            priceCurrency: proCheckout.year.currency.toUpperCase(),
            description: "Yearly, plus a 5% platform fee on membership sales.",
          },
        ],
      },
    ],
  };
}

export function websiteStructuredData(
  origin: string,
  name: string,
  description: string,
  socials: readonly { url: string }[] = [],
) {
  const sameAs = identityProfileUrls(socials);
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name,
    url: canonicalUrl(origin, "/"),
    ...(description ? { description } : {}),
    ...(sameAs.length ? { sameAs } : {}),
  } satisfies StructuredData;
}

/** A trail from the home page, ending at the current page. */
export function breadcrumbStructuredData(
  origin: string,
  trail: readonly (readonly [name: string, path: string])[],
) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: trail.map(([name, path], index) => ({
      "@type": "ListItem",
      position: index + 1,
      name,
      item: canonicalUrl(origin, path),
    })),
  } satisfies StructuredData;
}

/** The questions a page shows with their answers, word for word. */
export function faqStructuredData(faqs: readonly (readonly [question: string, answer: string])[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map(([question, answer]) => ({
      "@type": "Question",
      name: question,
      acceptedAnswer: { "@type": "Answer", text: answer },
    })),
  } satisfies StructuredData;
}

/** One of Drate's free tools, such as the Patreon fee calculator. */
export function webApplicationStructuredData(
  origin: string,
  tool: { name: string; path: string; description: string },
) {
  return {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: tool.name,
    url: canonicalUrl(origin, tool.path),
    description: tool.description,
    applicationCategory: "FinanceApplication",
    operatingSystem: "Web",
    isAccessibleForFree: true,
    offers: { "@type": "Offer", price: "0.00", priceCurrency: "USD" },
    publisher: { "@type": "Organization", name: "Drate", url: canonicalUrl(origin, "/") },
  } satisfies StructuredData;
}

/** The creator is named as a brand: Drate doesn't know whether a site is one person or a team. */
function creatorEntity(origin: string, name: string) {
  return { "@type": "Organization", name, url: canonicalUrl(origin, "/") };
}

export function postStructuredData(input: {
  origin: string;
  url: string;
  creatorName: string;
  headline: string;
  description: string;
  publishedAt: string | null;
  image?: string;
}) {
  const creator = creatorEntity(input.origin, input.creatorName);
  const published = input.publishedAt ? Date.parse(input.publishedAt) : Number.NaN;
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: input.headline.slice(0, 110),
    ...(input.description ? { description: input.description } : {}),
    url: input.url,
    mainEntityOfPage: input.url,
    ...(Number.isNaN(published) ? {} : { datePublished: new Date(published).toISOString() }),
    ...(input.image ? { image: input.image } : {}),
    author: creator,
    publisher: creator,
  } satisfies StructuredData;
}

export function courseStructuredData(input: {
  origin: string;
  url: string;
  creatorName: string;
  name: string;
  description: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Course",
    name: input.name,
    description: input.description || input.name,
    url: input.url,
    provider: creatorEntity(input.origin, input.creatorName),
  } satisfies StructuredData;
}
